import { useState, useRef, useEffect, useMemo } from "react";
import {
  Plus,
  Trash,
  FolderPlus,
  Image,
  Check,
  ChevronDown,
  ChevronRight,
  Upload,
  Star,
  ArrowRightLeft,
  CheckSquare,
  Square,
} from "lucide-react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { applyWatermark } from "../../utils/watermark";
import {
  GalleryPhoto,
  GalleryFolder,
  uploadTournamentPhotos,
  listTournamentPhotosPaginated,
  listTournamentFolders,
  deleteTournamentPhoto,
  getTournamentFolderThumbnails,
  setTournamentFolderThumbnail,
  deleteTournamentFolderThumbnail,
  moveTournamentPhoto,
  moveTournamentPhotos,
  getTournamentFolderPhotoCount,
} from "../../services/firebase";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { Button, Card, Dialog, EmptyState, IconButton, Input, Loader, Select, Skeleton, Spinner, Tag } from "../../ui";
import "./styles.scss";

interface TournamentGalleryManagerProps {
  tournamentName: string;
}

const PAGE_SIZE = 20;

const TournamentGalleryManager = ({ tournamentName }: TournamentGalleryManagerProps) => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [folders, setFolders] = useState<GalleryFolder[]>([]);
  const [rootPhotos, setRootPhotos] = useState<GalleryPhoto[]>([]);
  const [rootPhotoCount, setRootPhotoCount] = useState(0);
  const [folderPhotos, setFolderPhotos] = useState<Record<string, GalleryPhoto[]>>({});
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [rootThumbnail, setRootThumbnail] = useState<GalleryPhoto | null>(null);
  const [folderThumbnails, setFolderThumbnails] = useState<Record<string, GalleryPhoto>>({});
  const [rootPageToken, setRootPageToken] = useState<string | null>(null);
  const [rootLoadingMore, setRootLoadingMore] = useState(false);
  const [folderPageTokens, setFolderPageTokens] = useState<Record<string, string | null>>({});
  const [folderLoadingMore, setFolderLoadingMore] = useState<Record<string, boolean>>({});
  const [folderLoaded, setFolderLoaded] = useState<Record<string, boolean>>({});
  const [folderPhotoCounts, setFolderPhotoCounts] = useState<Record<string, number>>({});

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);

  const [selectedPhoto, setSelectedPhoto] = useState<GalleryPhoto | null>(null);
  const [selectedPhotoFolder, setSelectedPhotoFolder] = useState<string | null>(null);
  const [moveTargetFolder, setMoveTargetFolder] = useState<string | null>(null);
  const [deletingPhoto, setDeletingPhoto] = useState<string | null>(null);
  const [movingPhoto, setMovingPhoto] = useState(false);
  const [settingThumbnail, setSettingThumbnail] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [bulkMoveMode, setBulkMoveMode] = useState(false);
  const [bulkMoveTarget, setBulkMoveTarget] = useState<string | null | "ROOT">(null);
  const [bulkMoving, setBulkMoving] = useState(false);
  const [bulkMoveProgress, setBulkMoveProgress] = useState({ current: 0, total: 0 });

  const reservedFolderNames = useMemo(() => new Set<string>(), []);

  const loadGalleryData = async () => {
    setLoading(true);
    try {
      const [loadedFolders, thumbnails, rootPage, rootCount] = await Promise.all([
        listTournamentFolders(tournamentName),
        getTournamentFolderThumbnails(tournamentName),
        listTournamentPhotosPaginated(tournamentName, undefined, undefined, PAGE_SIZE),
        getTournamentFolderPhotoCount(tournamentName),
      ]);

      setFolders(loadedFolders);
      setRootPhotos(rootPage.photos);
      setRootPageToken(rootPage.nextPageToken);
      setRootPhotoCount(rootCount);
      setRootThumbnail(thumbnails.root);
      setFolderThumbnails(thumbnails.folders);

      // Initialize folder states
      const initialFolderPhotos: Record<string, GalleryPhoto[]> = {};
      const initialFolderPageTokens: Record<string, string | null> = {};
      const initialFolderLoaded: Record<string, boolean> = {};
      const initialFolderCounts: Record<string, number> = {};

      // Preload first page of photos and get counts for each folder
      const folderLoadPromises = loadedFolders.map(async (folder) => {
        try {
          const [folderPage, count] = await Promise.all([
            listTournamentPhotosPaginated(tournamentName, folder.name, undefined, PAGE_SIZE),
            getTournamentFolderPhotoCount(tournamentName, folder.name),
          ]);
          initialFolderPhotos[folder.name] = folderPage.photos;
          initialFolderPageTokens[folder.name] = folderPage.nextPageToken;
          initialFolderLoaded[folder.name] = true;
          initialFolderCounts[folder.name] = count;
        } catch (error) {
          console.error(`Error loading folder ${folder.name}:`, error);
          initialFolderPhotos[folder.name] = [];
          initialFolderPageTokens[folder.name] = null;
          initialFolderLoaded[folder.name] = false;
          initialFolderCounts[folder.name] = 0;
        }
      });

      await Promise.all(folderLoadPromises);

      setFolderPhotos(initialFolderPhotos);
      setFolderPageTokens(initialFolderPageTokens);
      setFolderLoaded(initialFolderLoaded);
      setFolderPhotoCounts(initialFolderCounts);
      setFolderLoadingMore({});
    } catch (error) {
      console.error("Error loading gallery:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGalleryData();
  }, [tournamentName]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });

    try {
      const fileArray = Array.from(files);
      const watermarkedFiles = await Promise.all(fileArray.map((f) => applyWatermark(f, tournamentName)));
      const uploadedPhotos = await uploadTournamentPhotos(
        tournamentName,
        watermarkedFiles,
        selectedFolder || undefined,
        (current, total) => setUploadProgress({ current, total })
      );

      // Prepend newly uploaded photos so they appear first, then update counts
      if (selectedFolder) {
        const newCount = await getTournamentFolderPhotoCount(tournamentName, selectedFolder);
        setFolderPhotoCounts((prev) => ({
          ...prev,
          [selectedFolder]: newCount,
        }));
        setFolderPhotos((prev) => ({
          ...prev,
          [selectedFolder]: [...uploadedPhotos.reverse(), ...(prev[selectedFolder] || [])],
        }));
        setFolderLoaded((prev) => ({ ...prev, [selectedFolder]: true }));
      } else {
        const rootCount = await getTournamentFolderPhotoCount(tournamentName);
        setRootPhotos((prev) => [...uploadedPhotos.reverse(), ...prev]);
        setRootPhotoCount(rootCount);
      }

      setSelectedFolder(null);
    } catch (error) {
      console.error("Error uploading photos:", error);
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleCreateFolder = async () => {
    const trimmedName = newFolderName.trim();
    if (!trimmedName || reservedFolderNames.has(trimmedName)) return;

    const newFolder: GalleryFolder = {
      name: trimmedName,
      path: `tournament-photos/${tournamentName}/${trimmedName}`,
    };

    setFolders([...folders, newFolder]);
    setFolderPhotos({ ...folderPhotos, [trimmedName]: [] });
    setNewFolderName("");
    setShowCreateFolder(false);
  };

  const clearFolderThumbnailIfMatch = async (folderName: string | null, photo: GalleryPhoto) => {
    if (folderName === null) {
      // Root folder
      if (rootThumbnail?.fullPath === photo.fullPath) {
        await deleteTournamentFolderThumbnail(tournamentName, undefined);
      }
    } else {
      // Regular folder
      const thumbnail = folderThumbnails[folderName];
      if (thumbnail?.fullPath === photo.fullPath) {
        await deleteTournamentFolderThumbnail(tournamentName, folderName);
      }
    }
  };

  const handleDeletePhoto = async (photo: GalleryPhoto, folderName: string | null) => {
    setDeletingPhoto(photo.fullPath);
    try {
      await clearFolderThumbnailIfMatch(folderName, photo);
      await deleteTournamentPhoto(photo.fullPath);

      // Update state without resetting pagination
      if (folderName) {
        setFolderPhotos((prev) => ({
          ...prev,
          [folderName]: (prev[folderName] || []).filter((p) => p.fullPath !== photo.fullPath),
        }));
        // Update count
        setFolderPhotoCounts((prev) => ({
          ...prev,
          [folderName]: Math.max(0, (prev[folderName] || 0) - 1),
        }));
      } else {
        setRootPhotos((prev) => prev.filter((p) => p.fullPath !== photo.fullPath));
        setRootPhotoCount((prev) => Math.max(0, prev - 1));
      }

      setSelectedPhoto(null);
      setSelectedPhotoFolder(null);
    } catch (error) {
      console.error("Error deleting photo:", error);
      // On error, reload to ensure consistency
      await loadGalleryData();
    } finally {
      setDeletingPhoto(null);
    }
  };

  const loadFolderPhotos = async (folderName: string, reset = false) => {
    setFolderLoadingMore((prev) => ({ ...prev, [folderName]: true }));
    const pageToken = reset ? undefined : folderPageTokens[folderName] || undefined;
    try {
      const page = await listTournamentPhotosPaginated(
        tournamentName,
        folderName,
        pageToken,
        PAGE_SIZE
      );
      setFolderPhotos((prev) => ({
        ...prev,
        [folderName]: reset ? page.photos : [...(prev[folderName] || []), ...page.photos],
      }));
      setFolderPageTokens((prev) => ({
        ...prev,
        [folderName]: page.nextPageToken,
      }));
      setFolderLoaded((prev) => ({ ...prev, [folderName]: true }));
    } catch (error) {
      console.error("Error loading folder photos:", error);
    } finally {
      setFolderLoadingMore((prev) => ({ ...prev, [folderName]: false }));
    }
  };

  const toggleFolder = (folderName: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderName)) {
      newExpanded.delete(folderName);
    } else {
      newExpanded.add(folderName);
      // Photos are already preloaded, no need to load again
    }
    setExpandedFolders(newExpanded);
  };

  const handleLoadMoreRoot = async () => {
    if (!rootPageToken) return;
    setRootLoadingMore(true);
    try {
      const page = await listTournamentPhotosPaginated(
        tournamentName,
        undefined,
        rootPageToken,
        PAGE_SIZE
      );
      setRootPhotos((prev) => [...prev, ...page.photos]);
      setRootPageToken(page.nextPageToken);
    } catch (error) {
      console.error("Error loading more root photos:", error);
    } finally {
      setRootLoadingMore(false);
    }
  };

  const handleUploadToFolder = (folderName: string | null) => {
    setSelectedFolder(folderName);
    fileInputRef.current?.click();
  };

  const handleSelectPhoto = (photo: GalleryPhoto, folderName: string | null, e?: React.MouseEvent) => {
    if (bulkMoveMode && e) {
      e.stopPropagation();
      const newSelected = new Set(selectedPhotos);
      if (newSelected.has(photo.fullPath)) {
        newSelected.delete(photo.fullPath);
      } else {
        newSelected.add(photo.fullPath);
      }
      setSelectedPhotos(newSelected);
      return;
    }
    setSelectedPhoto(photo);
    setSelectedPhotoFolder(folderName);
    setMoveTargetFolder(folderName);
  };

  const toggleBulkMoveMode = () => {
    setBulkMoveMode(!bulkMoveMode);
    setSelectedPhotos(new Set());
    setSelectedPhoto(null);
  };

  const selectAllInSection = (photos: GalleryPhoto[]) => {
    const newSelected = new Set(selectedPhotos);
    photos.forEach((photo) => newSelected.add(photo.fullPath));
    setSelectedPhotos(newSelected);
  };

  const deselectAllInSection = (photos: GalleryPhoto[]) => {
    const newSelected = new Set(selectedPhotos);
    photos.forEach((photo) => newSelected.delete(photo.fullPath));
    setSelectedPhotos(newSelected);
  };

  const isAllSelectedInSection = (photos: GalleryPhoto[]) => {
    return photos.length > 0 && photos.every((photo) => selectedPhotos.has(photo.fullPath));
  };

  const handleBulkMove = async () => {
    if (selectedPhotos.size === 0 || !bulkMoveTarget) return;

    const photosToMove: { photo: GalleryPhoto; folderName: string | null }[] = [];

    // Collect all selected photos with their current folder
    rootPhotos.forEach((photo) => {
      if (selectedPhotos.has(photo.fullPath)) {
        photosToMove.push({ photo, folderName: null });
      }
    });

    Object.entries(folderPhotos).forEach(([folderName, photos]) => {
      photos.forEach((photo) => {
        if (selectedPhotos.has(photo.fullPath)) {
          photosToMove.push({ photo, folderName });
        }
      });
    });

    if (photosToMove.length === 0) return;

    setBulkMoving(true);
    setBulkMoveProgress({ current: 0, total: photosToMove.length });

    try {
      // Clear thumbnails for photos being moved
      for (const { photo, folderName } of photosToMove) {
        await clearFolderThumbnailIfMatch(folderName, photo);
      }

      // Move all photos
      const photos = photosToMove.map(({ photo }) => photo);
      const targetFolder = bulkMoveTarget === "ROOT" ? undefined : bulkMoveTarget || undefined;
      const movedPhotos = await moveTournamentPhotos(
        tournamentName,
        photos,
        targetFolder,
        (current, total) => setBulkMoveProgress({ current, total })
      );

      // Update state without resetting pagination
      // Remove from sources
      photosToMove.forEach(({ photo, folderName }) => {
        if (folderName) {
          setFolderPhotos((prev) => ({
            ...prev,
            [folderName]: (prev[folderName] || []).filter((p) => p.fullPath !== photo.fullPath),
          }));
        } else {
          setRootPhotos((prev) => prev.filter((p) => p.fullPath !== photo.fullPath));
        }
      });

      // Add to destination
      if (targetFolder) {
        setFolderPhotos((prev) => ({
          ...prev,
          [targetFolder]: [...(prev[targetFolder] || []), ...movedPhotos],
        }));
        // Mark folder as loaded if it wasn't before
        if (!folderLoaded[targetFolder]) {
          setFolderLoaded((prev) => ({ ...prev, [targetFolder]: true }));
        }
        // Update count
        setFolderPhotoCounts((prev) => ({
          ...prev,
          [targetFolder]: (prev[targetFolder] || 0) + photosToMove.length,
        }));
      } else {
        setRootPhotos((prev) => [...prev, ...movedPhotos]);
        setRootPhotoCount((prev) => prev + photosToMove.length);
      }

      // Update source counts
      photosToMove.forEach(({ folderName }) => {
        if (folderName) {
          setFolderPhotoCounts((prev) => ({
            ...prev,
            [folderName]: Math.max(0, (prev[folderName] || 0) - 1),
          }));
        } else {
          setRootPhotoCount((prev) => Math.max(0, prev - 1));
        }
      });

      setSelectedPhotos(new Set());
      setBulkMoveMode(false);
      setBulkMoveTarget(null);

      // Show success message
      const destination = targetFolder || "root";
      dispatch(
        setGeneralAlertMessage({
          message: `Successfully moved ${photosToMove.length} photo${photosToMove.length !== 1 ? "s" : ""} to ${destination}`,
          type: "success",
        })
      );
    } catch (error) {
      console.error("Error moving photos:", error);
      dispatch(
        setGeneralAlertMessage({
          message: "Failed to move photos. Please try again.",
          type: "error",
        })
      );
    } finally {
      setBulkMoving(false);
      setBulkMoveProgress({ current: 0, total: 0 });
    }
  };

  const handleMovePhoto = async () => {
    if (!selectedPhoto) return;
    if (moveTargetFolder === selectedPhotoFolder) return;

    setMovingPhoto(true);
    try {
      await clearFolderThumbnailIfMatch(selectedPhotoFolder, selectedPhoto);
      const movedPhoto = await moveTournamentPhoto(
        tournamentName,
        selectedPhoto,
        moveTargetFolder || undefined
      );

      // Update state without resetting pagination
      // Remove from source
      if (selectedPhotoFolder) {
        setFolderPhotos((prev) => ({
          ...prev,
          [selectedPhotoFolder!]: (prev[selectedPhotoFolder!] || []).filter(
            (p) => p.fullPath !== selectedPhoto.fullPath
          ),
        }));
      } else {
        setRootPhotos((prev) => prev.filter((p) => p.fullPath !== selectedPhoto.fullPath));
      }

      // Add to destination
      if (moveTargetFolder) {
        setFolderPhotos((prev) => ({
          ...prev,
          [moveTargetFolder]: [...(prev[moveTargetFolder] || []), movedPhoto],
        }));
        // Mark folder as loaded if it wasn't before
        if (!folderLoaded[moveTargetFolder]) {
          setFolderLoaded((prev) => ({ ...prev, [moveTargetFolder]: true }));
        }
        // Update count
        setFolderPhotoCounts((prev) => ({
          ...prev,
          [moveTargetFolder]: (prev[moveTargetFolder] || 0) + 1,
        }));
      } else {
        setRootPhotos((prev) => [...prev, movedPhoto]);
        setRootPhotoCount((prev) => prev + 1);
      }

      // Update source count
      if (selectedPhotoFolder) {
        setFolderPhotoCounts((prev) => ({
          ...prev,
          [selectedPhotoFolder]: Math.max(0, (prev[selectedPhotoFolder] || 0) - 1),
        }));
      } else {
        setRootPhotoCount((prev) => Math.max(0, prev - 1));
      }

      setSelectedPhoto(movedPhoto);
      setSelectedPhotoFolder(moveTargetFolder);

      // Show success message
      const destination = moveTargetFolder || "root";
      dispatch(
        setGeneralAlertMessage({
          message: `Photo moved to ${destination} successfully`,
          type: "success",
        })
      );
    } catch (error) {
      console.error("Error moving photo:", error);
      dispatch(
        setGeneralAlertMessage({
          message: "Failed to move photo. Please try again.",
          type: "error",
        })
      );
      // On error, reload to ensure consistency
      await loadGalleryData();
    } finally {
      setMovingPhoto(false);
    }
  };

  const handleSetThumbnail = async () => {
    if (!selectedPhoto) return;
    setSettingThumbnail(true);
    try {
      await setTournamentFolderThumbnail(
        tournamentName,
        selectedPhotoFolder || undefined,
        selectedPhoto
      );

      // Update thumbnail without resetting pagination
      // Reload thumbnails only
      const thumbnails = await getTournamentFolderThumbnails(tournamentName);
      setRootThumbnail(thumbnails.root);
      setFolderThumbnails(thumbnails.folders);

      // The photo was renamed to "thumbnail", so remove it from the current list
      if (selectedPhotoFolder) {
        setFolderPhotos((prev) => ({
          ...prev,
          [selectedPhotoFolder]: (prev[selectedPhotoFolder] || []).filter(
            (p) => p.fullPath !== selectedPhoto.fullPath
          ),
        }));
      } else {
        // Root folder
        setRootPhotos((prev) => prev.filter((p) => p.fullPath !== selectedPhoto.fullPath));
      }

      dispatch(
        setGeneralAlertMessage({
          message: selectedPhotoFolder ? "Folder thumbnail updated." : "Root thumbnail updated.",
          type: "success",
        })
      );
    } catch (error) {
      console.error("Error setting folder thumbnail:", error);
      // On error, reload to ensure consistency
      await loadGalleryData();
    } finally {
      setSettingThumbnail(false);
    }
  };

  const totalPhotos = rootPhotoCount + Object.values(folderPhotoCounts).reduce((sum, count) => sum + count, 0);
  const folderOptions = useMemo(() => folders.map((folder) => folder.name), [folders]);

  // Keep the photo dialog's content steady while it animates closed
  const lastSelectedPhoto = useRef<GalleryPhoto | null>(null);
  const lastSelectedPhotoFolder = useRef<string | null>(null);
  if (selectedPhoto) {
    lastSelectedPhoto.current = selectedPhoto;
    lastSelectedPhotoFolder.current = selectedPhotoFolder;
  }
  const shownPhoto = selectedPhoto ?? lastSelectedPhoto.current;
  const shownPhotoFolder = selectedPhoto ? selectedPhotoFolder : lastSelectedPhotoFolder.current;

  // A photo tile in the bulk / preview grid (bespoke: a photo you click, a tick box in bulk mode, a delete chip)
  const renderPhotoTile = (photo: GalleryPhoto, folderName: string | null) => {
    const isSelected = selectedPhotos.has(photo.fullPath);
    return (
      <div
        key={photo.fullPath}
        className={`photo-thumbnail ${isSelected ? "selected" : ""}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSelectPhoto(photo, folderName, e);
        }}
        style={{ pointerEvents: "auto" }}
      >
        {bulkMoveMode && (
          <span className={`selection-checkbox ${isSelected ? "is-checked" : ""}`} aria-hidden="true">
            {isSelected && <Check size={14} strokeWidth={3} />}
          </span>
        )}
        <LazyLoadImage
          src={photo.url}
          alt={photo.name}
          effect="blur"
          wrapperClassName="lazy-image-wrapper"
          threshold={100}
          placeholder={<Skeleton className="image-skeleton" height="100%" />}
          loading="lazy"
          crossOrigin="anonymous"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = "none";
          }}
        />
        {!bulkMoveMode && (
          <div className="photo-overlay">
            <button
              type="button"
              className="delete-photo-btn"
              aria-label="Delete photo"
              onClick={(e) => {
                e.stopPropagation();
                handleDeletePhoto(photo, folderName);
              }}
              disabled={deletingPhoto === photo.fullPath}
            >
              {deletingPhoto === photo.fullPath ? <Spinner size="sm" /> : <Trash size={14} />}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card
      className="gallery-manager"
      title={
        <span className="gallery-manager__title">
          GALLERY
          {totalPhotos > 0 && <Tag tone="spark">{totalPhotos}</Tag>}
        </span>
      }
      actions={
        <>
          {bulkMoveMode && selectedPhotos.size > 0 && (
            <span className="bulk-selection-info">{selectedPhotos.size} selected</span>
          )}
          <IconButton
            label="Bulk move photos"
            variant={bulkMoveMode ? "tint" : "ghost"}
            aria-pressed={bulkMoveMode}
            icon={bulkMoveMode ? <CheckSquare size={18} /> : <Square size={18} />}
            onClick={toggleBulkMoveMode}
          />
          <IconButton label="Create folder" icon={<FolderPlus size={18} />} onClick={() => setShowCreateFolder(true)} />
          <IconButton
            className="gallery-upload-btn"
            label="Upload photos"
            variant="tint"
            icon={<Upload size={18} />}
            onClick={() => handleUploadToFolder(null)}
          />
        </>
      }
      footer={
        bulkMoveMode && selectedPhotos.size > 0 ? (
          <div className="bulk-move-bar">
            <div className="bulk-move-info">
              <span className="bulk-move-count">
                {selectedPhotos.size} photo{selectedPhotos.size !== 1 ? "s" : ""} selected
              </span>
              {bulkMoving && (
                <span className="bulk-move-progress">
                  Moving {bulkMoveProgress.current} of {bulkMoveProgress.total}...
                </span>
              )}
            </div>
            <div className="bulk-move-controls">
              <Select
                className="bulk-move-select"
                aria-label="Move to"
                value={bulkMoveTarget === "ROOT" ? "ROOT" : bulkMoveTarget || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setBulkMoveTarget(value === "" ? null : value === "ROOT" ? "ROOT" : value);
                }}
                disabled={bulkMoving}
              >
                <option value="">Select destination...</option>
                <option value="ROOT">Root</option>
                {folderOptions.map((folder) => (
                  <option key={folder} value={folder}>
                    {folder}
                  </option>
                ))}
              </Select>
              <Button
                icon={<ArrowRightLeft size={16} />}
                loading={bulkMoving}
                onClick={handleBulkMove}
                disabled={bulkMoving || bulkMoveTarget === null}
              >
                {bulkMoving ? (
                  "Moving..."
                ) : (
                  <>
                    Move {selectedPhotos.size} photo{selectedPhotos.size !== 1 ? "s" : ""}
                  </>
                )}
              </Button>
              <Button variant="ghost" onClick={toggleBulkMoveMode} disabled={bulkMoving}>
                Cancel
              </Button>
            </div>
          </div>
        ) : undefined
      }
    >
      {/* One hidden file chooser shared by every kit upload button: each sets its target folder, then opens it */}
      <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileSelect} hidden />

      {loading ? (
        <div className="gallery-skeleton" role="status" aria-label="Loading">
          <div className="skeleton-section-header">
            <Skeleton width={28} height={28} radius={4} />
            <Skeleton width={120} height={14} />
          </div>
          <div className="photo-grid">
            {Array.from({ length: 24 }).map((_, i) => (
              <div key={i} className="photo-thumbnail">
                <Skeleton className="image-skeleton" height="100%" />
              </div>
            ))}
          </div>
        </div>
      ) : uploading ? (
        <div className="upload-progress-container">
          <div className="upload-progress-info">
            <Upload size={22} />
            <p>
              Uploading {uploadProgress.current} of {uploadProgress.total} photos...
            </p>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
            />
          </div>
        </div>
      ) : totalPhotos === 0 && folders.length === 0 ? (
        <EmptyState
          icon={<Image />}
          title="No photos yet"
          description="Upload photos to create a gallery for this tournament"
          action={
            <Button icon={<Plus size={16} />} onClick={() => handleUploadToFolder(null)}>
              Upload photos
            </Button>
          }
        />
      ) : (
        <div className="gallery-content">
          {rootPhotos.length > 0 && (
            <div className="photo-section">
              <div className="section-header">
                <div className="section-title-wrapper">
                  {rootThumbnail ? (
                    <LazyLoadImage
                      className="root-thumbnail"
                      src={rootThumbnail.url}
                      alt="Root"
                      effect="blur"
                      wrapperClassName="lazy-image-wrapper"
                      threshold={50}
                      placeholder={<Skeleton width={28} height={28} radius={4} />}
                      loading="lazy"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="root-thumbnail placeholder">
                      <Image size={16} />
                    </div>
                  )}
                  <span className="section-title">Root</span>
                  {bulkMoveMode && (
                    <Button
                      variant="link"
                      size="sm"
                      className="select-all-btn"
                      onClick={() =>
                        isAllSelectedInSection(rootPhotos)
                          ? deselectAllInSection(rootPhotos)
                          : selectAllInSection(rootPhotos)
                      }
                    >
                      {isAllSelectedInSection(rootPhotos) ? "Deselect all" : "Select all"}
                    </Button>
                  )}
                </div>
                <Button variant="tint" size="sm" icon={<Plus size={14} />} onClick={() => handleUploadToFolder(null)}>
                  Add
                </Button>
              </div>
              <div className="photo-grid">{rootPhotos.map((photo) => renderPhotoTile(photo, null))}</div>
              {rootPageToken && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="load-more-btn"
                  onClick={handleLoadMoreRoot}
                  disabled={rootLoadingMore}
                >
                  {rootLoadingMore ? "Loading..." : "Load more"}
                </Button>
              )}
            </div>
          )}

          {folders.map((folder) => {
            const folderPhotoList = folderPhotos[folder.name] || [];
            const folderThumbnail = folderThumbnails[folder.name] || folderPhotoList[0];
            return (
              <div key={folder.path} className="folder-section">
                {/* A disclosure row (the whole row toggles the folder; Add uploads into it) */}
                <div
                  className={`folder-header ${expandedFolders.has(folder.name) ? "is-open" : ""}`}
                  onClick={() => toggleFolder(folder.name)}
                >
                  <div className="folder-title-wrapper">
                    {expandedFolders.has(folder.name) ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    {folderThumbnail ? (
                      <LazyLoadImage
                        className="folder-thumbnail"
                        src={folderThumbnail.url}
                        alt={folder.name}
                        effect="blur"
                        wrapperClassName="lazy-image-wrapper"
                        threshold={50}
                        placeholder={<Skeleton width={28} height={28} radius={4} />}
                        loading="lazy"
                        crossOrigin="anonymous"
                      />
                    ) : (
                      <div className="folder-thumbnail placeholder">
                        <Image size={16} />
                      </div>
                    )}
                    <span className="folder-name">{folder.name}</span>
                  </div>
                  <Button
                    variant="tint"
                    size="sm"
                    icon={<Plus size={14} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUploadToFolder(folder.name);
                    }}
                  >
                    Add
                  </Button>
                </div>
                {expandedFolders.has(folder.name) && (
                  <div className="folder-content">
                    {folderLoadingMore[folder.name] && folderPhotoList.length === 0 ? (
                      <Loader minHeight={140} label="Loading photos..." />
                    ) : folderPhotoList.length > 0 ? (
                      <>
                        {bulkMoveMode && (
                          <div className="section-select-all">
                            <Button
                              variant="link"
                              size="sm"
                              className="select-all-btn"
                              onClick={() =>
                                isAllSelectedInSection(folderPhotoList)
                                  ? deselectAllInSection(folderPhotoList)
                                  : selectAllInSection(folderPhotoList)
                              }
                            >
                              {isAllSelectedInSection(folderPhotoList) ? "Deselect all" : "Select all"}
                            </Button>
                          </div>
                        )}
                        <div className="photo-grid">
                          {folderPhotoList.map((photo) => renderPhotoTile(photo, folder.name))}
                        </div>
                        {folderPageTokens[folder.name] && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="load-more-btn"
                            onClick={() => loadFolderPhotos(folder.name)}
                            disabled={folderLoadingMore[folder.name]}
                          >
                            {folderLoadingMore[folder.name] ? "Loading..." : "Load more"}
                          </Button>
                        )}
                      </>
                    ) : (
                      <EmptyState compact icon={<Image />} title="No photos in this folder" />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog
        open={showCreateFolder}
        onClose={() => setShowCreateFolder(false)}
        title="Create New Folder"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowCreateFolder(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || reservedFolderNames.has(newFolderName.trim())}
            >
              Create Folder
            </Button>
          </>
        }
      >
        <Input
          type="text"
          aria-label="Folder name"
          placeholder="Folder name (e.g., Day 1, Finals)"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
          autoFocus
        />
      </Dialog>

      <Dialog
        open={!!selectedPhoto}
        onClose={() => setSelectedPhoto(null)}
        size="xl"
        className="gallery-photo-dialog"
        bodyClassName="gallery-photo-dialog__body"
        footer={
          shownPhoto ? (
            <div className="gallery-photo-dialog__footer">
              {folders.length > 0 && (
                <div className="photo-move-actions">
                  <span className="photo-move-label">Move to</span>
                  <Select
                    size="sm"
                    className="photo-move-select"
                    aria-label="Move to"
                    value={moveTargetFolder || ""}
                    onChange={(e) => setMoveTargetFolder(e.target.value || null)}
                  >
                    <option value="">Root</option>
                    {folderOptions.map((folder) => (
                      <option key={folder} value={folder}>
                        {folder}
                      </option>
                    ))}
                  </Select>
                  <Button
                    size="sm"
                    icon={<ArrowRightLeft size={14} />}
                    loading={movingPhoto}
                    onClick={handleMovePhoto}
                    disabled={movingPhoto || moveTargetFolder === selectedPhotoFolder}
                  >
                    {movingPhoto ? "Moving..." : "Move"}
                  </Button>
                </div>
              )}
              <div className="photo-preview-actions">
                <Button
                  variant="tint"
                  icon={<Star size={16} />}
                  loading={settingThumbnail}
                  onClick={handleSetThumbnail}
                  disabled={settingThumbnail}
                >
                  {settingThumbnail ? "Setting..." : <>Set as {shownPhotoFolder ? "folder" : "root"} thumbnail</>}
                </Button>
                <Button
                  variant="danger"
                  icon={<Trash size={16} />}
                  loading={deletingPhoto === shownPhoto.fullPath}
                  onClick={() => selectedPhoto && handleDeletePhoto(selectedPhoto, selectedPhotoFolder)}
                  disabled={deletingPhoto === shownPhoto.fullPath}
                >
                  {deletingPhoto === shownPhoto.fullPath ? "Deleting..." : "Delete Photo"}
                </Button>
              </div>
            </div>
          ) : undefined
        }
      >
        {shownPhoto && (
          <div className="gallery-photo-dialog__image">
            <LazyLoadImage
              src={shownPhoto.url}
              alt={shownPhoto.name}
              effect="blur"
              wrapperClassName="lazy-image-wrapper"
              threshold={200}
              placeholder={<Skeleton className="gallery-photo-dialog__skeleton" height={400} />}
              loading="eager"
              crossOrigin="anonymous"
            />
          </div>
        )}
      </Dialog>
    </Card>
  );
};

export default TournamentGalleryManager;
